/**
 * TGW-1510: 楽観UI 状態遷移ロジックの単体テスト。
 *
 * 仕様: tasks/TGW-1510.md §4.1 の 10 ケース。
 * - #7/#8 は欠陥①/②の回帰テスト（修正前 fail / 修正後 pass）。
 */

import {
  WS_ACK_TIMEOUT_MS,
  classifyAjaxFailure,
  setMessageStatus,
  applyIncomingMessage,
  createPendingTimeouts,
} from './optimistic_status';

describe('classifyAjaxFailure', () => {
  // #1
  test('422 (validation error) → failed', () => {
    expect(classifyAjaxFailure({ status: 422 })).toBe('failed');
  });

  // #2
  test('0 (network down) / 500 / 503 → unconfirmed', () => {
    expect(classifyAjaxFailure({ status: 0 })).toBe('unconfirmed');
    expect(classifyAjaxFailure({ status: 500 })).toBe('unconfirmed');
    expect(classifyAjaxFailure({ status: 503 })).toBe('unconfirmed');
  });

  // #3
  test('boundary: 400 / 499 → failed', () => {
    expect(classifyAjaxFailure({ status: 400 })).toBe('failed');
    expect(classifyAjaxFailure({ status: 499 })).toBe('failed');
  });

  // #4
  test('status field absent → unconfirmed', () => {
    expect(classifyAjaxFailure({})).toBe('unconfirmed');
    expect(classifyAjaxFailure(null)).toBe('unconfirmed');
    expect(classifyAjaxFailure(undefined)).toBe('unconfirmed');
  });
});

describe('setMessageStatus', () => {
  test('updates only the matching client_id message', () => {
    const messages = [
      { id: 1, client_id: 'c1', status: 'sending', text: 'a' },
      { id: 2, client_id: 'c2', status: 'sending', text: 'b' },
    ];
    const next = setMessageStatus(messages, 'c1', 'unconfirmed');
    expect(next[0].status).toBe('unconfirmed');
    expect(next[1].status).toBe('sending');
    // 元配列を破壊しない
    expect(messages[0].status).toBe('sending');
  });
});

describe('applyIncomingMessage', () => {
  // #5
  test('client_id matched → replaces with sent (count unchanged)', () => {
    const messages = [
      { id: null, client_id: 'c1', status: 'sending', text: 'optimistic' },
    ];
    const incoming = { id: 99, client_id: 'c1', text: 'persisted' };
    const next = applyIncomingMessage(messages, incoming, 'c1');
    expect(next).toHaveLength(1);
    expect(next[0]).toEqual({
      id: 99,
      client_id: 'c1',
      text: 'persisted',
      status: 'sent',
    });
  });

  // #6
  test('client_id mismatched but id already present → does not append', () => {
    const messages = [
      { id: 50, client_id: 'cX', status: 'sent', text: 'existing' },
    ];
    const incoming = { id: 50, client_id: 'cY', text: 'dup' };
    const next = applyIncomingMessage(messages, incoming, 'cZ');
    expect(next).toHaveLength(1);
    expect(next[0].id).toBe(50);
  });

  test('client_id absent and id new → appends with sent', () => {
    const messages = [{ id: 1, status: 'sent', text: 'a' }];
    const incoming = { id: 2, text: 'b' };
    const next = applyIncomingMessage(messages, incoming, null);
    expect(next).toHaveLength(2);
    expect(next[1]).toEqual({ id: 2, text: 'b', status: 'sent' });
  });
});

describe('createPendingTimeouts', () => {
  beforeEach(() => {
    jest.useFakeTimers();
  });
  afterEach(() => {
    jest.clearAllTimers();
    jest.useRealTimers();
  });

  // #7 (回帰①: 欠陥① — WS 到着で clear すれば 30s 後も発火しない)
  test('clear after arm prevents callback even after WS_ACK_TIMEOUT_MS', () => {
    const reg = createPendingTimeouts();
    const cb = jest.fn();
    reg.arm('c1', cb);
    reg.clear('c1');
    jest.advanceTimersByTime(WS_ACK_TIMEOUT_MS + 1000);
    expect(cb).not.toHaveBeenCalled();
  });

  // #8 (回帰②: 欠陥② — タイムアウト発火時、unconfirmed を立てる)
  test('arm fires after WS_ACK_TIMEOUT_MS and sets unconfirmed (not failed)', () => {
    const reg = createPendingTimeouts();
    let messages = [
      { id: null, client_id: 'c1', status: 'sending', text: 'x' },
    ];
    reg.arm('c1', () => {
      messages = setMessageStatus(messages, 'c1', 'unconfirmed');
    });
    jest.advanceTimersByTime(WS_ACK_TIMEOUT_MS);
    expect(messages[0].status).toBe('unconfirmed');
    expect(messages[0].status).not.toBe('failed');
  });

  // #9
  test('arm twice on the same clientId → only the latest callback fires once', () => {
    const reg = createPendingTimeouts();
    const cbOld = jest.fn();
    const cbNew = jest.fn();
    reg.arm('c1', cbOld);
    reg.arm('c1', cbNew);
    jest.advanceTimersByTime(WS_ACK_TIMEOUT_MS + 1000);
    expect(cbOld).not.toHaveBeenCalled();
    expect(cbNew).toHaveBeenCalledTimes(1);
  });

  // #10
  test('clearAll prevents all pending callbacks from firing', () => {
    const reg = createPendingTimeouts();
    const cb1 = jest.fn();
    const cb2 = jest.fn();
    reg.arm('c1', cb1);
    reg.arm('c2', cb2);
    reg.clearAll();
    jest.advanceTimersByTime(WS_ACK_TIMEOUT_MS + 1000);
    expect(cb1).not.toHaveBeenCalled();
    expect(cb2).not.toHaveBeenCalled();
    // タイマーリーク無し
    expect(jest.getTimerCount()).toBe(0);
  });

  test('after callback fires naturally, registry is empty (no leak)', () => {
    const reg = createPendingTimeouts();
    reg.arm('c1', () => {});
    jest.advanceTimersByTime(WS_ACK_TIMEOUT_MS);
    // 発火後、内部 map から削除されている → clear しても二重解除にならない
    reg.clear('c1');
    expect(jest.getTimerCount()).toBe(0);
  });
});
